/**
 * #/flush-server — bumps the shared cache epoch, so EVERY client drops its cache on
 * its next revalidation.
 *
 * Admin only, enforced by the server with a 403; the check below only decides what
 * to draw. Deliberately behind a button rather than running on arrival like
 * #/flush-all: this one affects every visitor, and a URL that fires it on load could
 * be triggered by a link someone pasted into a chat.
 */
import { useState } from 'react';
import { useFlushServer } from '../data/flush';
import { useAuth } from '../auth/AuthProvider';
import { SCREENS } from './routes';
import { ADMIN_ROLE } from '@framework/core';
import type { ServerFlush } from '../data/types';

const MESSAGE: Record<ServerFlush, string> = {
  done: 'Epoch bumped. Every client drops its cache on the next revalidation.',
  denied: 'Refused: this is admin only.',
  failed: 'The server could not be reached. Nothing was flushed.',
};

export function FlushServer({ onClose }: { onClose: () => void }) {
  const flush = useFlushServer();
  const { session } = useAuth();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ServerFlush | null>(null);

  const isAdmin = session?.roles.includes(ADMIN_ROLE) ?? false;

  async function run() {
    setBusy(true);
    setResult(await flush());
    setBusy(false);
  }

  return (
    <div className="flags-page">
      <div className="container flags-inner flags-narrow">
        <header className="flags-head">
          <div>
            <h1>Flush server cache</h1>
            <p>Affects every visitor, not just you.</p>
          </div>
          <button onClick={onClose}>← Back to site</button>
        </header>

        {!isAdmin ? (
          <p className="flags-denied">
            Admin only. Your own browser cache is yours to clear at any time —{' '}
            <a href={SCREENS.flushLocal}>#/flush-all</a>.
          </p>
        ) : (
          <>
            <ul className="flush-list">
              <li className={result === 'done' ? 'done' : ''}>
                <strong>Everyone</strong>
                <span>
                  The shared epoch changes. Every client notices on its next query and
                  drops its cache without waiting out the 30-minute TTL.
                </span>
              </li>
              <li className="skipped">
                <strong>Content</strong>
                <span>Nothing is deleted or rewritten. Caches are dropped; the database is untouched.</span>
              </li>
            </ul>

            {result && (
              <p className={result === 'done' ? 'notice-ok' : 'ctl-error'}>{MESSAGE[result]}</p>
            )}

            <div className="flags-foot">
              <button className="primary" onClick={() => void run()} disabled={busy}>
                {busy ? 'Flushing…' : 'Flush for everyone'}
              </button>
              <p>
                To clear only your own browser, use{' '}
                <a href={SCREENS.flushLocal}>#/flush-all</a> — no session required.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
